// Strategy config, PMS credentials, Ollama config API calls.

import { apiFetch } from "./apiClient";
import { readUnreadableSpecialDateRefusal } from "./specialDateRefusal";
import {
    loadedConnectionQuery,
    refusedWriteError,
    type LoadedConnectionId,
} from "./loadedConnection";

import type {
    AppConfig,
    PmsCredentials,
    PmsCredentialsResponse,
    PmsHealthResponse,
    OllamaConfig,
    WellKnownModel,
    OllamaLocalModel,
    PullStatus,
} from "../types/config";

// -- Config -----------------------------------------------------------------

/**
 * The open property's strategies, and the property they were read for. The
 * open property is shared by every tab, so the page names it back when it saves
 * (`services/loadedConnection.ts`) and compares it with the property open when
 * it is looked at again. Undefined where the service did not say which.
 */
export interface LoadedConfig extends AppConfig {
    connectionId: LoadedConnectionId;
}

export async function loadConfig(): Promise<LoadedConfig> {
    const res = await apiFetch("/api/config");
    if (!res.ok) throw new Error(`loadConfig failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to load config");
    return {
        ...data.config,
        connectionId: typeof data.connectionId === "number" ? data.connectionId : undefined,
    };
}

/**
 * Save the open property's strategies. A save replaces the property's whole
 * set, so `loadedConnectionId` names the property they were loaded for, and the
 * service refuses them with `ActiveConnectionChangedError` once another one is
 * open. The body carries the strategies alone: they are all the service reads,
 * and the property's name travels in the query.
 */
export async function saveConfig(config: AppConfig, loadedConnectionId: LoadedConnectionId): Promise<void> {
    const res = await apiFetch(`/api/config${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ strategies: config.strategies }),
    });
    // A special date the engine could never match: the editor names the rows.
    const refusal = await readUnreadableSpecialDateRefusal(res);
    if (refusal) throw refusal;
    if (!res.ok) throw await refusedWriteError(res, "saveConfig failed");
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to save config");
}

// -- Property Management Credentials ----------------------------------------

export async function fetchPmsCredentials(): Promise<PmsCredentialsResponse> {
    const res = await apiFetch("/api/pms-credentials");
    if (!res.ok)
        throw new Error(`fetchPmsCredentials failed: ${res.status}`);
    return res.json();
}

/**
 * The property a credential form was loaded for, as `fetchPmsCredentials`
 * named it (`services/loadedConnection.ts`).
 */
export type { LoadedConnectionId };

export interface SavedPmsCredentials {
    /**
     * The property the save wrote, created by it when none was open. The form
     * belongs to it from then on. Absent from a service released before the
     * field.
     */
    connectionId?: number;
}

/**
 * Save the credential form over the open property, or create one with none
 * open. `loadedConnectionId` names the property the form was loaded for, and
 * the service refuses the form with `ActiveConnectionChangedError` once
 * another one is open.
 */
export async function savePmsCredentials(
    creds: PmsCredentials,
    connectionName: string | undefined,
    currency: string | undefined,
    providerType: string | undefined,
    loadedConnectionId: LoadedConnectionId,
): Promise<SavedPmsCredentials> {
    const body: Record<string, unknown> = { ...creds, connectionName, currency };
    if (providerType) body.providerType = providerType;
    const res = await apiFetch(`/api/pms-credentials${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await refusedWriteError(res, "savePmsCredentials failed");
    const data = await res.json().catch(() => ({}));
    return { connectionId: typeof data.connectionId === "number" ? data.connectionId : undefined };
}

export async function checkPmsHealth(): Promise<PmsHealthResponse> {
    const res = await apiFetch("/api/pms-credentials/health");
    if (!res.ok)
        throw new Error(`checkPmsHealth failed: ${res.status}`);
    return res.json();
}

/**
 * Test the credential form without saving it. A masked secret is filled in
 * from the open property, so `loadedConnectionId` names the property the form
 * was loaded for, as a save does.
 */
export async function testPmsCredentials(
    creds: PmsCredentials,
    providerType: string | undefined,
    loadedConnectionId: LoadedConnectionId,
): Promise<PmsHealthResponse> {
    const body: Record<string, unknown> = { ...creds };
    if (providerType) body.providerType = providerType;
    const res = await apiFetch(`/api/pms-credentials/test${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await refusedWriteError(res, "testPmsCredentials failed");
    return res.json();
}

// -- Ollama Config (admin-only) ---------------------------------------------

export async function fetchOllamaConfig(): Promise<OllamaConfig> {
    const res = await apiFetch("/api/ollama-config");
    if (!res.ok) {
        if (res.status === 403) throw new Error("Admin access required");
        throw new Error(`fetchOllamaConfig failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.success)
        throw new Error(data.error || "Failed to load Ollama config");
    return data.ollamaConfig;
}

export async function saveOllamaConfig(config: {
    OLLAMA_API_BASE: string;
    OLLAMA_API_KEY?: string;
    OLLAMA_MODEL?: string;
}): Promise<void> {
    const res = await apiFetch("/api/ollama-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
    });
    if (!res.ok) {
        if (res.status === 403) throw new Error("Admin access required");
        throw new Error(`saveOllamaConfig failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.success)
        throw new Error(data.error || "Failed to save Ollama config");
}

export interface OllamaHealthResponse {
    success: boolean;
    healthy: boolean;
    ollamaUrl: string;
    message: string;
}

export async function checkOllamaHealth(): Promise<OllamaHealthResponse> {
    const res = await apiFetch("/api/ollama-config/health");
    if (!res.ok) {
        if (res.status === 403) throw new Error("Admin access required");
        throw new Error(`checkOllamaHealth failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.success)
        throw new Error(data.error || "Failed to check Ollama health");
    return data;
}

// -- Ollama Model Management (admin-only) -----------------------------------

export async function fetchWellKnownModels(): Promise<WellKnownModel[]> {
    const res = await apiFetch("/api/ollama-config/well-known-models");
    if (!res.ok) {
        if (res.status === 403) throw new Error("Admin access required");
        throw new Error(`fetchWellKnownModels failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.success)
        throw new Error(data.error || "Failed to load well-known models");
    return data.models;
}

export async function fetchOllamaModels(): Promise<OllamaLocalModel[]> {
    const res = await apiFetch("/api/ollama-config/models");
    if (!res.ok) {
        if (res.status === 403) throw new Error("Admin access required");
        if (res.status === 502) return [];
        throw new Error(`fetchOllamaModels failed: ${res.status}`);
    }
    const data = await res.json();
    if (!data.success) return [];
    return data.models;
}

export async function pullOllamaModel(model: string): Promise<void> {
    const res = await apiFetch("/api/ollama-config/models/pull", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model }),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(data.error || `pullOllamaModel failed: ${res.status}`);
    }
}

export async function fetchPullStatus(): Promise<PullStatus> {
    const res = await apiFetch("/api/ollama-config/models/pull-status");
    if (!res.ok) {
        throw new Error(`fetchPullStatus failed: ${res.status}`);
    }
    const data = await res.json();
    return data;
}
